import React, { useMemo } from 'react';
import { DashboardBookingRow } from '../../../services/dashboard';

const paymentColors = {
  Paid: '#68D464',
  Pending: '#FFCA50',
  Unpaid: '#DE4545',
};

type DashboardBookingsProps = {
  bookings: DashboardBookingRow[];
  filterQuery?: string;
  onClearSearch?: () => void;
};

function matchesQuery(row: DashboardBookingRow, query: string): boolean {
  const q = query.toLowerCase();
  return [row.id, row.user, row.tech, row.contact, row.slot, row.status, row.payment].some(
    (field) => field?.toLowerCase().includes(q),
  );
}

export function DashboardBookings({ bookings, filterQuery = '', onClearSearch }: DashboardBookingsProps) {
  const filtered = useMemo(() => {
    const q = filterQuery.trim();
    if (!q) return bookings;
    return bookings.filter((row) => matchesQuery(row, q));
  }, [bookings, filterQuery]);

  return (
    <div
      style={{
        background: '#181818',
        borderRadius: 16,
        color: '#F6F6F6',
        fontFamily: 'Inter, Arial, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          letterSpacing: '-0.01em',
          padding: '18px 20px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span>All Bookings</span>
        {filterQuery.trim() && (
          <button
            type="button"
            onClick={onClearSearch}
            style={{
              fontSize: 12,
              color: '#aaa',
              fontWeight: 500,
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: 8,
              padding: '4px 10px',
              cursor: 'pointer',
            }}
          >
            {filtered.length} of {bookings.length} · Clear
          </button>
        )}
      </div>
      {filtered.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            color: '#8A8D91',
            padding: '24px 14px 32px',
            fontSize: 15,
          }}
        >
          {filterQuery.trim() ? 'No bookings match your search' : 'No recent bookings'}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr
              style={{
                background: '#232323',
                color: '#D6D6D6',
                fontSize: 13,
                fontWeight: 500,
                textAlign: 'center',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            >
              <th style={{ padding: '10px 0', borderTopLeftRadius: 8 }}>Booking ID</th>
              <th style={{ padding: '10px 0' }}>User Name</th>
              <th style={{ padding: '10px 0' }}>Technician Name</th>
              <th style={{ padding: '10px 0' }}>User Contact</th>
              <th style={{ padding: '10px 0' }}>Booking Slot</th>
              <th style={{ padding: '10px 0' }}>Status</th>
              <th style={{ padding: '10px 0', borderTopRightRadius: 8 }}>Payment</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, i) => (
              <React.Fragment key={`${row.id}-${i}`}>
                <tr>
                  <td colSpan={7} style={{ height: 8, background: 'transparent', padding: 0, border: 'none' }} />
                </tr>
                <tr
                  style={{
                    background: '#181818',
                    borderTop: '1px solid #232323',
                    borderBottom: '1px solid #232323',
                  }}
                >
                  <td style={{ padding: '8px 0', fontSize: 15, color: '#8A8D91', textAlign: 'center', border: 'none' }}>
                    {row.id}
                  </td>
                  <td style={{ padding: '8px 0', fontSize: 15, color: '#8A8D91', textAlign: 'center', border: 'none' }}>
                    {row.user}
                  </td>
                  <td style={{ padding: '8px 0', fontSize: 15, color: '#8A8D91', textAlign: 'center', border: 'none' }}>
                    {row.tech}
                  </td>
                  <td style={{ padding: '8px 0', fontSize: 15, color: '#8A8D91', textAlign: 'center', border: 'none' }}>
                    {row.contact}
                  </td>
                  <td style={{ padding: '8px 0', fontSize: 15, color: '#8A8D91', textAlign: 'center', border: 'none' }}>
                    {row.slot}
                  </td>
                  <td style={{ padding: '8px 0', fontSize: 13, color: '#8A8D91', textAlign: 'center', border: 'none' }}>
                    {row.status?.replace(/_/g, ' ') || '—'}
                  </td>
                  <td style={{ padding: '8px 0', fontSize: 15, color: '#8A8D91', textAlign: 'center', border: 'none' }}>
                    <span
                      style={{
                        color: paymentColors[row.payment as keyof typeof paymentColors],
                        fontWeight: 500,
                      }}
                    >
                      {row.payment}
                    </span>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ height: 12 }} />
    </div>
  );
}
